// The nav as a real table of contents, not a switch: dataset, group and
// leaf section all live in one rail at once (indented, not collapsed into
// each other), and picking a leaf just scrolls the main content to it —
// every leaf of the active group stays rendered and open, mirroring how
// ReportSidebar.jsx jumps between report sections rather than hiding them.
// Reused as-is for both the sticky desktop rail and the mobile drawer in
// RTKayttoikaKysely.jsx — this component only renders rows, it never
// decides how it's positioned.
//
// Stops at the leaf level on purpose — a fourth level listing every
// question inside the active leaf was tried and reverted: it made the
// rail too dense (a leaf like Lämmöntuotanto alone has 11 questions).
// Group/leaf names are source-table terms and a few carry a long
// "(esim. ...)" parenthetical clarifying what's included (e.g.
// "Päätelaitteet (esim. tuloilmalaitteet, poistoilmalaitteet, ...)") —
// fine in the main content heading, but it crowds the narrow rail.
// Stripped for display only; the full name is still there as a tooltip.
function shortLabel(text) {
  return text.replace(/\s*\([^)]*\)/g, '').trim();
}

function countFor(questions, answers) {
  const answered = questions.filter(q => answers[q.code]).length;
  return { answered, total: questions.length };
}

function CountBadge({ answered, total }) {
  const done = answered === total && total > 0;
  return (
    <span style={{
      fontSize: '0.68rem', fontWeight: 600, color: done ? '#198754' : '#8e8e93',
      background: done ? '#eafbf1' : '#f2f2f7', borderRadius: 999, padding: '2px 8px', flexShrink: 0,
    }}>
      {answered}/{total}
    </span>
  );
}

const rowStyle = (depth, active) => ({
  display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left',
  border: 'none', background: active ? '#eafbf1' : 'none', cursor: 'pointer',
  borderLeft: `3px solid ${active ? '#198754' : 'transparent'}`,
  padding: `${depth === 0 ? 9 : 7}px 12px ${depth === 0 ? 9 : 7}px ${12 + depth * 12}px`,
  fontSize: depth === 0 ? '0.82rem' : depth === 1 ? '0.78rem' : '0.73rem',
  fontWeight: depth === 0 ? 700 : active ? 700 : 600,
  color: active ? '#198754' : '#1c1c1e',
  transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
});

// Expand/collapse the next level with a real slide instead of a hard
// pop — the group/leaf list is always in the DOM (hiding it with `&&`
// unmounts it, which can't be animated), its wrapper just collapses to
// 0 height. `grid-template-rows: 0fr→1fr` animates height without
// knowing the content's real height up front, which a plain `max-height`
// transition can't do reliably once the list's length varies.
function Collapsible({ open, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows 0.2s ease' }}>
      <div style={{ overflow: 'hidden', minHeight: 0 }}>{children}</div>
    </div>
  );
}

export default function RTKayttoikaSidebar({ datasets, activeDatasetKey, activeGroup, activeLeafIndex, answers, onSelectDataset, onSelectGroup, onSelectLeaf }) {
  return (
    <div>
      <div style={{ background: '#198754', color: '#fff', padding: '12px 14px', fontWeight: 800, fontSize: '0.86rem' }}>
        Käyttöiät ja kunnossapito
      </div>

      {datasets.map(ds => {
        const isActiveDs = ds.key === activeDatasetKey;
        const dsCount = countFor(ds.questions, answers);
        return (
          <div key={ds.key}>
            <button type="button" onClick={() => onSelectDataset(ds.key)} style={rowStyle(0, isActiveDs)}>
              <span style={{ flex: 1 }}>{ds.label}</span>
              <CountBadge {...dsCount} />
            </button>

            <Collapsible open={isActiveDs}>
              {ds.tree.map(g => {
                const isActiveGroup = g.group === activeGroup;
                const groupCount = countFor(g.leafSections.flatMap(ls => ls.questions), answers);
                return (
                  <div key={g.group}>
                    <button type="button" onClick={() => onSelectGroup(g.group)} title={g.group} style={rowStyle(1, isActiveGroup)}>
                      <span style={{ flex: 1 }}>{shortLabel(g.group)}</span>
                      <CountBadge {...groupCount} />
                    </button>

                    <Collapsible open={isActiveGroup}>
                      {g.leafSections.map((ls, li) => (
                        <button key={ls.leaf} type="button" onClick={() => onSelectLeaf(li)} title={ls.leaf} style={rowStyle(2, li === activeLeafIndex)}>
                          <span style={{ flex: 1 }}>{shortLabel(ls.leaf)}</span>
                          <CountBadge {...countFor(ls.questions, answers)} />
                        </button>
                      ))}
                    </Collapsible>
                  </div>
                );
              })}
            </Collapsible>
          </div>
        );
      })}
    </div>
  );
}
